import React from 'react'
import {
  Plus, Search, Filter, Library, FileText, BarChart3, Settings,
  LayoutGrid, List, Download, Upload, Eye, ChevronDown,
  Sparkles, Users, Target, TrendingUp,
  CheckCircle2, AlertCircle, X, ArrowUpDown, MoreVertical,
  Clock, ThumbsUp, ThumbsDown, FileSpreadsheet, Wrench, Loader2
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Label } from '@/components/ui/label'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import ObjectiveLibraryDialog from '@/components/admin/ObjectiveLibraryDialog'
import ImprovedObjectiveWizard from '@/components/performance/objective-setting/ImprovedObjectiveWizard'
import { apiClient } from '@/services/apiClient'
import toast from 'react-hot-toast'
import { STATUS_CONFIGS, TEMPLATE_TYPE_CONFIGS } from '@/utils/templateImportUtils'
import { useTemplateManagement } from '@/hooks/useTemplateManagement'
import TemplateImportDialog from '@/components/admin/template-management/TemplateImportDialog'
import TemplateFormDialog from '@/components/admin/template-management/TemplateFormDialog'
import ObjectivesTabContent from '@/components/admin/template-management/ObjectivesTabContent'
import TemplatesTabContent from '@/components/admin/template-management/TemplatesTabContent'
import ExportEmployeeObjectivesDialog from '@/components/admin/template-management/ExportEmployeeObjectivesDialog'

const TemplateManagementPage: React.FC = () => {
  const tm = useTemplateManagement()
  const [remediating, setRemediating] = React.useState(false)
  const [remediationResult, setRemediationResult] = React.useState<any>(null)
  const [showRemediationDialog, setShowRemediationDialog] = React.useState(false)
  const [exportObjectivesOpen, setExportObjectivesOpen] = React.useState(false)

  const handleRemediate = async (dryRun: boolean) => {
    setRemediating(true)
    try {
      const response = await apiClient.post<any>(`/api/v1/admin/remediate-strategic-context?dry_run=${dryRun}`)
      setRemediationResult(response.data)
      if (!dryRun) {
        toast.success(`Updated ${response.data.matched} objectives with strategic context`)
      }
    } catch (error: any) {
      console.error('Remediation error:', error)
      toast.error(error?.response?.data?.detail || 'Remediation failed')
    } finally {
      setRemediating(false)
    }
  }

  // Evidence submitter remediation (repair employee evidence locked by ID drift)
  const [evidenceRemediating, setEvidenceRemediating] = React.useState(false)
  const [evidenceRemediationResult, setEvidenceRemediationResult] = React.useState<any>(null)
  const [showEvidenceRemediationDialog, setShowEvidenceRemediationDialog] = React.useState(false)
  const [evidenceExcludeLoadtest, setEvidenceExcludeLoadtest] = React.useState(true)

  const handleEvidenceRemediate = async (dryRun: boolean, excludeLoadtest: boolean = evidenceExcludeLoadtest) => {
    setEvidenceRemediating(true)
    try {
      const response = await apiClient.post<any>(
        `/api/v1/admin/remediate-evidence-submitter?dry_run=${dryRun}&exclude_loadtest=${excludeLoadtest}`
      )
      setEvidenceRemediationResult(response.data)
      if (!dryRun) {
        toast.success(`Repaired ${response.data.repaired} impact portfolio records`)
      }
    } catch (error: any) {
      console.error('Evidence remediation error:', error)
      toast.error(error?.response?.data?.detail || 'Impact portfolio remediation failed')
    } finally {
      setEvidenceRemediating(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Library className="h-8 w-8 text-blue-600" />
              Template Management
            </h2>
            <p className="text-slate-600 mt-1">
              Manage objective templates for team member impact & growth planning
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button onClick={tm.handleCreateTemplate} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-t-4 border-t-blue-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Templates</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{tm.stats.total_templates}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {tm.stats.approved_templates} approved
                    </Badge>
                  </div>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-amber-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Pending Approval</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{tm.stats.pending_approval}</h3>
                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-600">
                    <Clock className="h-3 w-3 text-amber-600" />
                    <span>Requires attention</span>
                  </div>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-green-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Published</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{tm.stats.published_templates}</h3>
                  <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                    <TrendingUp className="h-3 w-3" />
                    <span>Available for use</span>
                  </div>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-purple-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Most Used</p>
                  <h3 className="text-lg font-bold text-slate-900 mt-2 line-clamp-1">
                    {tm.stats.most_used_template || 'None'}
                  </h3>
                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-600">
                    <Users className="h-3 w-3 text-purple-600" />
                    <span>{tm.stats.most_used_count} times</span>
                  </div>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader className="border-b border-slate-200">
            {/* Advanced Toolbar */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                {/* Search and Filters */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search templates by name or description..."
                      value={tm.searchQuery}
                      onChange={(e) => tm.setSearchQuery(e.target.value)}
                      className="pl-10 pr-16"
                    />
                    {tm.searchQuery && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {tm.filteredTemplates.length}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => tm.setSearchQuery('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <Button
                    variant={tm.showFilters || tm.activeFiltersCount > 0 ? "default" : "outline"}
                    size="sm"
                    onClick={() => tm.setShowFilters(!tm.showFilters)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                    {tm.activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-xs">
                        {tm.activeFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* View Mode Switcher */}
                <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-1">
                  <Button
                    variant={tm.viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => tm.setViewMode('grid')}
                    className="h-8"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={tm.viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => tm.setViewMode('list')}
                    className="h-8"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={tm.viewMode === 'compact' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => tm.setViewMode('compact')}
                    className="h-8"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </div>

                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={tm.handleOpenImportDialog}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Import Templates from Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={tm.handleDownloadTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Template (Excel)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setExportObjectivesOpen(true)}>
                      <Users className="h-4 w-4 mr-2" />
                      Export Team Member Objectives
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => {
                      setRemediationResult(null)
                      setShowRemediationDialog(true)
                      handleRemediate(true)
                    }}>
                      <Wrench className="h-4 w-4 mr-2" />
                      Remediate Strategic Context
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      setEvidenceRemediationResult(null)
                      setShowEvidenceRemediationDialog(true)
                      handleEvidenceRemediate(true)
                    }}>
                      <Wrench className="h-4 w-4 mr-2" />
                      Repair Impact Portfolio Submitters
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Filter Panel */}
              {tm.showFilters && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Filter Options</h4>
                    {tm.activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={tm.clearFilters} className="h-auto py-1 text-xs">
                        <X className="h-3 w-3 mr-1" />
                        Clear all ({tm.activeFiltersCount})
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {tm.activeTab === 'objectives' ? (
                      <>
                        {/* KPA Filter */}
                        <div>
                          <label className="text-xs font-medium text-slate-700 mb-2 block">
                            KPA
                            {tm.selectedObjKPAs.length > 0 && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {tm.selectedObjKPAs.length}
                              </Badge>
                            )}
                          </label>
                          <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                            <div className="space-y-2">
                              {tm.availableObjKPAs.map(kpa => (
                                <label key={kpa} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                                  <Checkbox
                                    checked={tm.selectedObjKPAs.includes(kpa)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        tm.setSelectedObjKPAs([...tm.selectedObjKPAs, kpa])
                                      } else {
                                        tm.setSelectedObjKPAs(tm.selectedObjKPAs.filter(k => k !== kpa))
                                      }
                                    }}
                                  />
                                  <span className="flex-1">{kpa}</span>
                                  <span className="text-xs text-slate-400">
                                    {tm.objectives.filter(obj => obj.kpa?.display_name === kpa).length}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>

                        {/* Pillar Filter */}
                        <div>
                          <label className="text-xs font-medium text-slate-700 mb-2 block">
                            Strategic Pillar
                            {tm.selectedObjPillars.length > 0 && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {tm.selectedObjPillars.length}
                              </Badge>
                            )}
                          </label>
                          <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                            <div className="space-y-2">
                              {tm.availableObjPillars.map(pillar => (
                                <label key={pillar} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                                  <Checkbox
                                    checked={tm.selectedObjPillars.includes(pillar)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        tm.setSelectedObjPillars([...tm.selectedObjPillars, pillar])
                                      } else {
                                        tm.setSelectedObjPillars(tm.selectedObjPillars.filter(p => p !== pillar))
                                      }
                                    }}
                                  />
                                  <span className="flex-1">{pillar}</span>
                                  <span className="text-xs text-slate-400">
                                    {tm.objectives.filter(obj => obj.pillar?.name === pillar).length}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>

                        {/* Status Filter for Objectives */}
                        <div>
                          <label className="text-xs font-medium text-slate-700 mb-2 block">
                            Status
                            {tm.selectedObjStatuses.length > 0 && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {tm.selectedObjStatuses.length}
                              </Badge>
                            )}
                          </label>
                          <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                            <div className="space-y-2">
                              {tm.availableObjStatuses.map(status => (
                                <label key={status} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                                  <Checkbox
                                    checked={tm.selectedObjStatuses.includes(status)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        tm.setSelectedObjStatuses([...tm.selectedObjStatuses, status])
                                      } else {
                                        tm.setSelectedObjStatuses(tm.selectedObjStatuses.filter(s => s !== status))
                                      }
                                    }}
                                  />
                                  <span className="flex-1 capitalize">{status.replace(/_/g, ' ')}</span>
                                  <span className="text-xs text-slate-400">
                                    {tm.objectives.filter(obj => obj.status === status).length}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>

                        {/* Library Status and Results */}
                        <div className="space-y-4">
                          <div>
                            <label className="text-xs font-medium text-slate-700 mb-2 block">Library Status</label>
                            <div className="space-y-2 bg-white rounded border border-slate-200 p-3">
                              <label className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                                <Checkbox
                                  checked={tm.showLibraryOnly}
                                  onCheckedChange={(checked) => tm.setShowLibraryOnly(checked as boolean)}
                                />
                                <span>In Library only</span>
                              </label>
                            </div>
                          </div>

                          {/* Quick Stats */}
                          <div>
                            <label className="text-xs font-medium text-slate-700 mb-2 block">Results</label>
                            <div className="bg-blue-50 rounded border border-blue-200 p-3 text-center">
                              <div className="text-2xl font-bold text-blue-900">
                                {tm.filteredObjectives.length}
                              </div>
                              <div className="text-xs text-blue-700">
                                of {tm.objectives.length} objectives
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                    {/* Template Type Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Template Type
                        {tm.selectedTypes.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {tm.selectedTypes.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {tm.availableTypes.map(type => (
                            <label key={type} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={tm.selectedTypes.includes(type)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    tm.setSelectedTypes([...tm.selectedTypes, type])
                                  } else {
                                    tm.setSelectedTypes(tm.selectedTypes.filter(t => t !== type))
                                  }
                                }}
                              />
                              <span className="flex-1 capitalize">{type.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {tm.templates.filter(t => t.template_type === type).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Status Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Status
                        {tm.selectedStatuses.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {tm.selectedStatuses.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {tm.availableStatuses.map(status => (
                            <label key={status} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={tm.selectedStatuses.includes(status)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    tm.setSelectedStatuses([...tm.selectedStatuses, status])
                                  } else {
                                    tm.setSelectedStatuses(tm.selectedStatuses.filter(s => s !== status))
                                  }
                                }}
                              />
                              <span className="flex-1 capitalize">{status.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {tm.templates.filter(t => t.status === status).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Role Type Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Role Type
                        {tm.selectedRoles.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {tm.selectedRoles.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {tm.availableRoles.map(role => (
                            <label key={role} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={tm.selectedRoles.includes(role)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    tm.setSelectedRoles([...tm.selectedRoles, role])
                                  } else {
                                    tm.setSelectedRoles(tm.selectedRoles.filter(r => r !== role))
                                  }
                                }}
                              />
                              <span className="flex-1 capitalize">{role.replace(/-/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {tm.templates.filter(t => t.role_type === role).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Published and Results */}
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-medium text-slate-700 mb-2 block">Visibility</label>
                        <div className="space-y-2 bg-white rounded border border-slate-200 p-3">
                          <label className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                            <Checkbox
                              checked={tm.showPublishedOnly}
                              onCheckedChange={(checked) => tm.setShowPublishedOnly(checked as boolean)}
                            />
                            <span>Published only</span>
                          </label>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div>
                        <label className="text-xs font-medium text-slate-700 mb-2 block">Results</label>
                        <div className="bg-blue-50 rounded border border-blue-200 p-3 text-center">
                          <div className="text-2xl font-bold text-blue-900">
                            {tm.filteredTemplates.length}
                          </div>
                          <div className="text-xs text-blue-700">
                            of {tm.templates.length} templates
                          </div>
                        </div>
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk Operations Bar */}
              {tm.selectedTemplates.size > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={tm.selectedTemplates.size === tm.filteredTemplates.length}
                      onCheckedChange={tm.handleSelectAll}
                    />
                    <span className="text-sm font-medium text-blue-900">
                      {tm.selectedTemplates.size} template{tm.selectedTemplates.size !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={tm.handleBulkDelete}>
                      <X className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => tm.setSelectedTemplates(new Set())}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tm.activeTab} onValueChange={(v) => tm.setActiveTab(v as any)}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="objectives" className="flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Objectives ({tm.objectives.length})
                </TabsTrigger>
                <TabsTrigger value="templates" className="flex items-center gap-2">
                  <Library className="h-4 w-4" />
                  All Templates ({tm.stats.total_templates})
                </TabsTrigger>
                <TabsTrigger value="pending" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Pending Approval
                  {tm.stats.pending_approval > 0 && (
                    <Badge variant="destructive" className="ml-1">
                      {tm.stats.pending_approval}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </TabsTrigger>
              </TabsList>

              {/* Objectives Tab */}
              <TabsContent value="objectives" className="mt-6">
                <ObjectivesTabContent
                  viewMode={tm.viewMode}
                  loading={tm.loading}
                  objectives={tm.objectives}
                  filteredObjectives={tm.filteredObjectives}
                  handleCreateObjective={tm.handleCreateObjective}
                  handleViewObjective={tm.handleViewObjective}
                  handleEditObjective={tm.handleEditObjective}
                  handleDeleteObjective={tm.handleDeleteObjective}
                  handleToggleLibraryObjective={tm.handleToggleLibraryObjective}
                  clearFilters={tm.clearFilters}
                />
              </TabsContent>

              <TabsContent value="templates" className="mt-6">
                <TemplatesTabContent
                  viewMode={tm.viewMode}
                  loading={tm.loading}
                  templates={tm.templates}
                  filteredTemplates={tm.filteredTemplates}
                  handlePreviewTemplate={tm.handlePreviewTemplate}
                  handleEditTemplate={tm.handleEditTemplate}
                  handleDeleteTemplate={tm.handleDeleteTemplate}
                  handlePublishToggle={tm.handlePublishToggle}
                  handleSubmitForApproval={tm.handleSubmitForApproval}
                  handlePublishDraft={tm.handlePublishDraft}
                  handleApproveClick={tm.handleApproveClick}
                  handleRejectClick={tm.handleRejectClick}
                  clearFilters={tm.clearFilters}
                />
              </TabsContent>

              <TabsContent value="pending" className="mt-6">
                <TemplatesTabContent
                  viewMode={tm.viewMode}
                  loading={tm.loading}
                  templates={tm.templates.filter(t => t.status === 'pending_approval')}
                  filteredTemplates={tm.filteredTemplates.filter(t => t.status === 'pending_approval')}
                  handlePreviewTemplate={tm.handlePreviewTemplate}
                  handleEditTemplate={tm.handleEditTemplate}
                  handleDeleteTemplate={tm.handleDeleteTemplate}
                  handlePublishToggle={tm.handlePublishToggle}
                  handleSubmitForApproval={tm.handleSubmitForApproval}
                  handlePublishDraft={tm.handlePublishDraft}
                  handleApproveClick={tm.handleApproveClick}
                  handleRejectClick={tm.handleRejectClick}
                  clearFilters={tm.clearFilters}
                />
              </TabsContent>

              <TabsContent value="analytics" className="mt-6">
                <div className="text-center py-16 bg-slate-50 rounded-lg">
                  <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Analytics Coming Soon
                  </h3>
                  <p className="text-slate-600">
                    Template usage analytics and insights will be available here
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={tm.previewDialogOpen} onOpenChange={tm.setPreviewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Template Preview
              </DialogTitle>
            </DialogHeader>

            {tm.previewTemplate && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-slate-900">{tm.previewTemplate.name}</h3>
                  <p className="text-slate-700 mt-2">{tm.previewTemplate.description}</p>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {(() => {
                      const statusConfig = STATUS_CONFIGS[tm.previewTemplate.status] || STATUS_CONFIGS['draft']
                      const typeConfig = TEMPLATE_TYPE_CONFIGS[tm.previewTemplate.template_type] || TEMPLATE_TYPE_CONFIGS['generic']
                      return (
                        <>
                          <Badge className={`${statusConfig.bgColor} ${statusConfig.color}`}>
                            {statusConfig.label}
                          </Badge>
                          <Badge className={`${typeConfig.bgColor} ${typeConfig.color}`}>
                            {tm.previewTemplate.template_type.replace(/_/g, ' ')}
                          </Badge>
                        </>
                      )
                    })()}
                    {tm.previewTemplate.is_published && (
                      <Badge className="bg-blue-100 text-blue-700">Published</Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-slate-600">Template Type</div>
                    <div className="font-semibold capitalize">{tm.previewTemplate.template_type.replace(/_/g, ' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Role Type</div>
                    <div className="font-semibold">{tm.previewTemplate.role_type || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Usage Count</div>
                    <div className="font-semibold">{tm.previewTemplate.usage_count} times</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Created</div>
                    <div className="font-semibold text-sm">{new Date(tm.previewTemplate.created_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {tm.previewTemplate.objectives_data?.objectives && tm.previewTemplate.objectives_data.objectives.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Target className="h-5 w-5 text-blue-600" />
                      Objectives ({tm.previewTemplate.objectives_data.objectives.length})
                    </h4>
                    <div className="space-y-3">
                      {tm.previewTemplate.objectives_data.objectives.map((obj: any, index: number) => (
                        <Card key={index}>
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3">
                              <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold text-slate-900">{obj.title}</h5>
                                <p className="text-sm text-slate-600 mt-1">{obj.description}</p>
                                {obj.kpaName && (
                                  <Badge variant="outline" className="mt-2">
                                    KPA: {obj.kpaName}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {tm.previewTemplate.status === 'approved' && tm.previewTemplate.approved_at && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                      <CheckCircle2 className="h-5 w-5" />
                      Approved Template
                    </div>
                    <div className="text-sm text-green-700">
                      Approved on {new Date(tm.previewTemplate.approved_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => tm.setPreviewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={tm.approvalDialogOpen} onOpenChange={tm.setApprovalDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {tm.approvalAction === 'approve' ? (
                  <>
                    <ThumbsUp className="h-5 w-5 text-green-600" />
                    Approve Template
                  </>
                ) : (
                  <>
                    <ThumbsDown className="h-5 w-5 text-red-600" />
                    Reject Template
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {tm.approvalAction === 'approve'
                  ? 'Review and approve this template for use across the institution'
                  : 'Provide a reason for rejecting this template'}
              </DialogDescription>
            </DialogHeader>

            {tm.approvalTemplate && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900">{tm.approvalTemplate.name}</h4>
                  <p className="text-sm text-slate-600 mt-1">{tm.approvalTemplate.description}</p>
                  <div className="flex gap-2 mt-3">
                    <Badge variant="outline">{tm.approvalTemplate.template_type.replace(/_/g, ' ')}</Badge>
                    {tm.approvalTemplate.role_type && (
                      <Badge variant="outline">{tm.approvalTemplate.role_type}</Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="approval-notes">
                    {tm.approvalAction === 'approve' ? 'Notes (optional)' : 'Rejection Reason *'}
                  </Label>
                  <Textarea
                    id="approval-notes"
                    placeholder={tm.approvalAction === 'approve'
                      ? "Add any notes or feedback..."
                      : "Explain why this template is being rejected..."}
                    value={tm.approvalNotes}
                    onChange={(e) => tm.setApprovalNotes(e.target.value)}
                    rows={4}
                  />
                </div>

                {tm.approvalAction === 'approve' && (
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox
                      id="publish-immediately"
                      checked={tm.publishImmediately}
                      onCheckedChange={(checked) => tm.setPublishImmediately(checked as boolean)}
                    />
                    <div>
                      <Label htmlFor="publish-immediately" className="font-medium cursor-pointer">
                        Publish Immediately
                      </Label>
                      <p className="text-xs text-slate-600">
                        Make this template available for use right away
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => tm.setApprovalDialogOpen(false)}
                disabled={tm.processing}
              >
                Cancel
              </Button>
              <Button
                onClick={tm.handleApprovalSubmit}
                disabled={tm.processing || (tm.approvalAction === 'reject' && !tm.approvalNotes.trim())}
                className={tm.approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {tm.processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    {tm.approvalAction === 'approve' ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Approve{tm.publishImmediately ? ' & Publish' : ''}
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Reject Template
                      </>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Creation/Edit Dialog */}
        <TemplateFormDialog
          templateDialogOpen={tm.templateDialogOpen}
          setTemplateDialogOpen={tm.setTemplateDialogOpen}
          editingTemplate={tm.editingTemplate}
          templateFormData={tm.templateFormData}
          setTemplateFormData={tm.setTemplateFormData}
          processing={tm.processing}
          handleCloseTemplateDialog={tm.handleCloseTemplateDialog}
          handleSaveTemplate={tm.handleSaveTemplate}
          templateDialogSearch={tm.templateDialogSearch}
          setTemplateDialogSearch={tm.setTemplateDialogSearch}
          templateDialogKpaFilter={tm.templateDialogKpaFilter}
          setTemplateDialogKpaFilter={tm.setTemplateDialogKpaFilter}
          templateDialogPillarFilter={tm.templateDialogPillarFilter}
          setTemplateDialogPillarFilter={tm.setTemplateDialogPillarFilter}
          templateDialogStatusFilter={tm.templateDialogStatusFilter}
          setTemplateDialogStatusFilter={tm.setTemplateDialogStatusFilter}
          resetTemplateDialogFilters={tm.resetTemplateDialogFilters}
          availableObjectives={tm.availableObjectives}
          loadingObjectives={tm.loadingObjectives}
          handleAddObjectiveToTemplate={tm.handleAddObjectiveToTemplate}
          handleRemoveObjectiveFromTemplate={tm.handleRemoveObjectiveFromTemplate}
          handleUpdateObjectiveFromLibrary={tm.handleUpdateObjectiveFromLibrary}
          handleRefreshAllFromLibrary={tm.handleRefreshAllFromLibrary}
        />

        {/* Objective View/Edit Dialog */}
        <ObjectiveLibraryDialog
          open={tm.objectiveDialogOpen}
          onOpenChange={tm.setObjectiveDialogOpen}
          objective={tm.selectedObjective}
          mode={tm.objectiveDialogMode}
          onSave={tm.handleUpdateObjective}
        />

        {/* Objective Creation Wizard */}
        <Dialog open={tm.showObjectiveWizard} onOpenChange={tm.setShowObjectiveWizard}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <ImprovedObjectiveWizard
              onSave={async (objectives) => {
                // Create each objective via API
                try {
                  for (const obj of objectives) {
                    // Get KPA info from the selected KPA
                    const kpaInfo = obj.kpa ? {
                      kpa_name: obj.kpa,
                      kpa_display_name: obj.kpaName,
                      pillar_name: obj.pillar
                    } : {}

                    const response = await apiClient.post('/api/v1/objectives/individual/', {
                      title: obj.title,
                      description: obj.description,
                      ...kpaInfo,
                      start_date: obj.startDate,
                      end_date: obj.endDate,
                      is_library_objective: true,
                      kpis: obj.kpis || []
                    })

                    if (response.status !== 200 && response.status !== 201) {
                      throw new Error((response.data as any)?.detail || 'Failed to create objective')
                    }
                  }

                  tm.handleObjectiveCreated()
                } catch (error: any) {
                  console.error('Error creating objectives:', error)
                  toast.error(error.message || 'Failed to create objectives')
                }
              }}
              onCancel={() => tm.setShowObjectiveWizard(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={tm.confirmDialog.isOpen}
          onClose={() => tm.setConfirmDialog({ ...tm.confirmDialog, isOpen: false })}
          onConfirm={tm.confirmDialog.onConfirm}
          title={tm.confirmDialog.title}
          message={tm.confirmDialog.message}
          type={tm.confirmDialog.type}
          confirmText={tm.confirmDialog.confirmText}
        />

        {/* Remediation Dialog */}
        <Dialog open={showRemediationDialog} onOpenChange={setShowRemediationDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-orange-600" />
                Remediate Strategic Context
              </DialogTitle>
              <DialogDescription>
                Scan objectives with missing L1-L3 strategic context and backfill from template data.
              </DialogDescription>
            </DialogHeader>

            {remediating && (
              <div className="flex items-center justify-center py-8 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-sm text-slate-600">Scanning objectives...</span>
              </div>
            )}

            {remediationResult && !remediating && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{remediationResult.total_missing}</p>
                      <p className="text-xs text-slate-500">Total Missing</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{remediationResult.matched}</p>
                      <p className="text-xs text-slate-500">Matched to Templates</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-amber-600">{remediationResult.unmatched}</p>
                      <p className="text-xs text-slate-500">Unmatched</p>
                    </CardContent>
                  </Card>
                </div>

                {remediationResult.dry_run && remediationResult.matched > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    This is a preview. Click "Apply Changes" to update {remediationResult.matched} objectives.
                  </div>
                )}

                {!remediationResult.dry_run && remediationResult.matched > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    Successfully updated {remediationResult.matched} objectives with strategic context.
                  </div>
                )}

                {remediationResult.updated_objectives?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-700">Matched objectives (sample):</p>
                    <ScrollArea className="h-48 rounded border">
                      <div className="p-2 space-y-1">
                        {remediationResult.updated_objectives.map((obj: any) => (
                          <div key={obj.id} className="text-xs flex justify-between py-1 border-b last:border-0">
                            <span className="font-medium truncate flex-1">{obj.title}</span>
                            <span className="text-slate-400 ml-2 shrink-0">from: {obj.matched_template}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemediationDialog(false)}>
                Close
              </Button>
              {remediationResult?.dry_run && remediationResult?.matched > 0 && (
                <Button
                  onClick={() => handleRemediate(false)}
                  disabled={remediating}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {remediating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
                  Apply Changes ({remediationResult.matched} objectives)
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Evidence Submitter Remediation Dialog */}
        <Dialog open={showEvidenceRemediationDialog} onOpenChange={setShowEvidenceRemediationDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-orange-600" />
                Repair Impact Portfolio Submitters
              </DialogTitle>
              <DialogDescription>
                Find impact portfolio records where the stored submitter ID doesn't match the
                impact portfolio owner (causing team members to be locked out of editing/deleting their own
                team-member-source impact portfolio). The repair sets <code>submitted_by = user_id</code> for
                affected team-member-source records. People Leader-source impact portfolio is never touched.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-between rounded border bg-slate-50 px-3 py-2 text-sm">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={evidenceExcludeLoadtest}
                    onChange={(e) => {
                      const next = e.target.checked
                      setEvidenceExcludeLoadtest(next)
                      // Re-run dry-run with new filter so the preview updates
                      handleEvidenceRemediate(true, next)
                    }}
                    className="h-4 w-4"
                  />
                  Exclude load test data
                </label>
                <p className="text-xs text-slate-500 mt-0.5 ml-6">
                  Hides records with <code>@loadtest.eduvos.com</code> or "Load test evidence" titles.
                </p>
              </div>
            </div>

            {evidenceRemediating && (
              <div className="flex items-center justify-center py-8 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-sm text-slate-600">Scanning impact portfolio records...</span>
              </div>
            )}

            {evidenceRemediationResult && !evidenceRemediating && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{evidenceRemediationResult.total_broken}</p>
                      <p className="text-xs text-slate-500">Broken Records</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{evidenceRemediationResult.users_affected}</p>
                      <p className="text-xs text-slate-500">Users Affected</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{evidenceRemediationResult.repaired}</p>
                      <p className="text-xs text-slate-500">Repaired</p>
                    </CardContent>
                  </Card>
                </div>

                {evidenceRemediationResult.dry_run && evidenceRemediationResult.total_broken > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    Preview only. Click "Apply Repair" to fix {evidenceRemediationResult.total_broken} records
                    across {evidenceRemediationResult.users_affected} user(s).
                  </div>
                )}

                {!evidenceRemediationResult.dry_run && evidenceRemediationResult.repaired > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    Successfully repaired {evidenceRemediationResult.repaired} impact portfolio records.
                    Affected users can now edit/delete their own impact portfolio.
                  </div>
                )}

                {evidenceRemediationResult.by_user?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-700">Per-user breakdown:</p>
                    <ScrollArea className="h-48 rounded border">
                      <div className="p-2 space-y-1">
                        {evidenceRemediationResult.by_user.map((entry: any) => (
                          <div key={entry.user_id} className="text-xs flex justify-between py-1 border-b last:border-0">
                            <span className="font-medium truncate flex-1">User: {entry.user_id}</span>
                            <span className="text-slate-500 ml-2 shrink-0">
                              {entry.count} record(s)
                              {entry.old_submitted_by_examples?.length > 0 && (
                                <span className="text-slate-400">
                                  {' '}(was: {entry.old_submitted_by_examples.slice(0, 2).join(', ')})
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEvidenceRemediationDialog(false)}>
                Close
              </Button>
              {evidenceRemediationResult?.dry_run && evidenceRemediationResult?.total_broken > 0 && (
                <Button
                  onClick={() => handleEvidenceRemediate(false)}
                  disabled={evidenceRemediating}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {evidenceRemediating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
                  Apply Repair ({evidenceRemediationResult.total_broken} records)
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Templates Dialog */}
        <TemplateImportDialog
          importDialogOpen={tm.importDialogOpen}
          setImportDialogOpen={tm.setImportDialogOpen}
          importStep={tm.importStep}
          setImportStep={tm.setImportStep}
          importFile={tm.importFile}
          importPreview={tm.importPreview}
          importProgress={tm.importProgress}
          importResults={tm.importResults}
          fileInputRef={tm.fileInputRef}
          handleCloseImportDialog={tm.handleCloseImportDialog}
          handleFileDrop={tm.handleFileDrop}
          handleFileInputChange={tm.handleFileInputChange}
          handleDownloadTemplate={tm.handleDownloadTemplate}
          handleImportTemplates={tm.handleImportTemplates}
          updateExistingTemplates={tm.updateExistingTemplates}
          setUpdateExistingTemplates={tm.setUpdateExistingTemplates}
        />

        {/* Export Employee Objectives Dialog — round-trips into the import above */}
        <ExportEmployeeObjectivesDialog
          open={exportObjectivesOpen}
          onOpenChange={setExportObjectivesOpen}
        />
      </div>
    </div>
  )
}

export default TemplateManagementPage
